/*
 *   File : webinarTemplate.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : Shared constants + helpers for the `webinar_invite` WhatsApp
 *                 template. Kept in a neutral module so the compose screen
 *                 (containers/notification) and the history modal
 *                 (components/modal) can build an identical preview without
 *                 duplicating logic. The single preview markup lives in
 *                 components/notification/webinarPreview.js.
 *   Version : v1.1
 */

// Header/banner image fallback (empty or broken image) is handled by the
// state-driven <FallbackImage> component, which renders a neutral placeholder
// container + centered image icon.

// The template is identified by its name/label from the `wa_templates` filter
// API (NOT a hardcoded numeric id, which differs per environment). The numeric
// `template_type` is sent straight from the selected option's value.
export const WEBINAR_TEMPLATE_NAME = "webinar_invite";

// webinar_invite exposes: 1 image header + a registration url + 7 editable
// content variables (content_one..content_seven). body_1 (recipient name) is
// auto-populated by the backend and is shown only as a {{name}} placeholder.
export const WEBINAR_CONTENT_COUNT = 7;
export const WEBINAR_CONTENT_KEYS = [
    "content_one",
    "content_two",
    "content_three",
    "content_four",
    "content_five",
    "content_six",
    "content_seven",
];

// Reuse the exact URL pattern already used for notification redirection URLs
// (see containers/notification/index.js). Keeps validation consistent.
const URL_REGEX = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/;

export const isValidWebinarUrl = (url) => URL_REGEX.test((url || "").trim());

// Registration URL is built from a fixed base + a user-entered suffix.
// e.g. suffix "abc123" -> "https://forms.gle/abc123".
export const WEBINAR_URL_PREFIX = "https://forms.gle/";

/**
 * cleanWebinarSuffix
 * Normalizes whatever the user typed/pasted into just the suffix:
 * - trims whitespace
 * - if they pasted the full forms.gle url, strips the known prefix
 * - removes any leading slashes
 * Keeps a pasted *different* full url intact (with its "://") so validation can reject it.
 */
export const cleanWebinarSuffix = (input) => {
    let s = (input || "").trim();
    if (s.toLowerCase().startsWith(WEBINAR_URL_PREFIX.toLowerCase())) {
        s = s.slice(WEBINAR_URL_PREFIX.length);
    }
    return s.replace(/^\/+/, "");
};

/**
 * buildWebinarUrl
 * Constructs the final registration url from a suffix. Returns "" for an empty
 * suffix so downstream validation treats it as invalid.
 */
export const buildWebinarUrl = (suffix) => {
    const s = (suffix || "").trim();
    return s ? WEBINAR_URL_PREFIX + s : "";
};

/**
 * isValidWebinarSuffix
 * Single source of truth for registration-url validity:
 * - empty is invalid
 * - a suffix still containing "://" means the user entered a full (non forms.gle)
 *   url -> invalid (ask for suffix only)
 * - otherwise validate the GENERATED final url, not just the suffix
 */
export const isValidWebinarSuffix = (suffix) => {
    const s = (suffix || "").trim();
    if (!s) return false;
    if (/:\/\//.test(s)) return false;
    return isValidWebinarUrl(buildWebinarUrl(s));
};

/**
 * isWebinarTemplate
 * Accepts the selected template option ({ label } / { name }) or a raw template
 * name string and returns true when it is the webinar template.
 *
 * Detection is by NAME, not numeric id (ids differ across environments). It is
 * tolerant of the display label the API actually returns ("Webinar Template")
 * as well as the slug ("webinar_invite"): a normalized, separator-stripped value
 * containing "webinar" matches.
 */
export const isWebinarTemplate = (template) => {
    if (!template) return false;
    const name = typeof template === "string"
        ? template
        : (template.label ?? template.name ?? template.value);
    if (name == null) return false;
    const normalized = String(name).toLowerCase().replace(/[\s_-]/g, "");
    return normalized === WEBINAR_TEMPLATE_NAME.replace(/[\s_-]/g, "")
        || normalized.includes("webinar");
};

const escapeHtml = (value) =>
    String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

/**
 * buildWebinarCaptionHtml
 * Builds the ready-to-render HTML caption for the webinar message bubble.
 * Slot mapping (body_1 = recipient name, auto-populated by backend):
 *   content_one   -> main title (bold)
 *   content_two   -> supporting description / subtitle
 *   content_three -> date & time (bold)
 *   content_four  -> speaker name
 *   content_five  -> learning point 1
 *   content_six   -> learning point 2
 *   content_seven -> learning point 3
 *
 * @param {string[]} contents - content_one..content_seven values. Empty values
 *                              fall back to a readable {{key}} placeholder.
 */
export const buildWebinarCaptionHtml = (contents = []) => {
    const value = (index, key) => {
        const raw = contents[index];
        return raw && String(raw).trim() !== "" ? escapeHtml(raw) : `{{${key}}}`;
    };

    return [
        `🎓 You're Invited to an Exclusive Webinar by MIRA Money! 🎯`,
        ``,
        `Hi {{name}},`,
        ``,
        `Join us for an insightful session on`,
        ``,
        `<b>${value(0, "content_one")}</b>`,
        ``,
        `${value(1, "content_two")}`,
        ``,
        `📅 <b>${value(2, "content_three")}</b>`,
        ``,
        `👤 Speaker: ${value(3, "content_four")}`,
        ``,
        `In this webinar, you'll learn:`,
        ``,
        `✅ ${value(4, "content_five")}`,
        `✅ ${value(5, "content_six")}`,
        `✅ ${value(6, "content_seven")}`,
        ``,
        `Click below to register and secure your seat.`,
        ``,
        `We look forward to seeing you!`,
        ``,
        `<span style="color:#646363ed">Yuga OS: India's first Behavioural Wealth OS</span>`,
    ].join("\n").replace(/\n/g, "<br>");
};

/**
 * getWebinarContentsFromData
 * Extracts content_one..content_seven from a saved/history whatsapp_data object
 * into an ordered array of length WEBINAR_CONTENT_COUNT.
 */
export const getWebinarContentsFromData = (whatsappData) => {
    if (!whatsappData) return Array(WEBINAR_CONTENT_COUNT).fill("");
    return WEBINAR_CONTENT_KEYS.map((key) => whatsappData[key] ?? "");
};

/**
 * getWebinarHeaderFromData
 * Resolves the header image url from a saved/history whatsapp_data object using
 * the payload shape: images: [{ image: url }].
 */
export const getWebinarHeaderFromData = (whatsappData) => {
    if (!whatsappData) return "";
    if (Array.isArray(whatsappData.images) && whatsappData.images[0]?.image) {
        return whatsappData.images[0].image;
    }
    return whatsappData.header || "";
};

export const getWebinarUrlFromData = (whatsappData) => whatsappData?.url || "";

/**
 * isWebinarData
 * Detects a webinar message from history/whatsapp_data without relying on a
 * hardcoded numeric id: prefers the label field, falls back to webinar-only
 * fields (content_two / registration url) that the general template never has.
 */
export const isWebinarData = (whatsappData) => {
    if (!whatsappData) return false;
    if (isWebinarTemplate(
        whatsappData.template_type_variable ?? whatsappData.template_name ?? whatsappData.template
    )) return true;
    return Boolean(whatsappData.content_two || (whatsappData.url && whatsappData.content_one));
};
